<script lang="ts" setup>
import { ref, watch } from 'vue';
import { useModalStore } from '@/store/modal.store';
import { useMarketingStore } from '@/store/marketing.store';
import DefaultBtn from "@/components/block/DefaultBtn.vue";

const props = defineProps<{
	closeSignal?: boolean
}>();

watch(() => props.closeSignal, () => {
	if (props.closeSignal) {
		close();
	}
});

const modalStore = useModalStore();
const marketingStore = useMarketingStore();

const loading = ref<boolean>(false);

const deleteLink = (): void => {
	if (!loading.value) {
		loading.value = true;
		
		const linkId = modalStore.modalPayload?.id;
		if (linkId) {
			marketingStore.deleteSignUpLink(Number(linkId))
				.then(() => {
					modalStore.closeModal();
				})
				.catch((error) => {
					console.error('Delete failed:', error);
				})
				.finally(() => {
					loading.value = false;
				});
		} else {
			loading.value = false;
		}
	}
};

const close = (): void => {
	if (!loading.value) {
		modalStore.closeModal();
	}
};
</script>

<template>
	<div class="modal">
		<div class="modal__body">
			<div class="modal__content">
				<h3 class="modal__title">Delete Marketing Link</h3>
				<p class="modal__text">
					Are you sure you want to delete this marketing link? This action cannot be undone.
				</p>
				<p class="modal__text">
					<strong>Link Name:</strong> {{ modalStore.modalPayload?.name || 'Unknown' }}
				</p>
				<p class="modal__text">
					<strong>Link ID:</strong> {{ modalStore.modalPayload?.id || 'Unknown' }}
				</p>
				
				<div class="modal__btn">
					<DefaultBtn 
						:text="'Cancel'" 
						:type="'button'" 
						:class="'btn--white'"
						@click="close"
						:disabled="loading"
					/>
					<DefaultBtn 
						:text="'Delete'" 
						:type="'button'" 
						:class="'btn--red'"
						@click="deleteLink"
						:loading="loading"
					/>
				</div>
			</div>
		</div>
	</div>
</template>

<style scoped>
.modal__body {
	width: 500px;
}

.modal__title {
	font-size: 22px;
	font-weight: 700;
	margin-bottom: 20px;
	color: #1f2937;
}

.modal__text {
	font-size: 16px;
	line-height: 1.5;
	margin-bottom: 15px;
	color: #6b7280;
}

.modal__text:last-of-type {
	margin-bottom: 30px;
}

.modal__btn {
	display: flex;
	gap: 15px;
	margin-top: 30px;
}

.modal__btn button {
	flex: 1;
}

.btn--white {
	background: #fff;
	border: 1px solid #e5e7eb;
	color: #374151;
}

.btn--white:hover {
	background: #f9fafb;
}

.btn--red {
	background: #dc2626;
	border: 1px solid #dc2626;
	color: #fff;
}

.btn--red:hover {
	background: #b91c1c;
	border-color: #b91c1c;
}

.btn--red:disabled {
	background: #9ca3af;
	border-color: #9ca3af;
	cursor: not-allowed;
}
</style>
